import type { Edge, Node } from '@xyflow/react';
import type { EditorValidationState } from './useEditorValidationState';

export interface SaveValidationErrors {
  nodeIds: Set<string>;
  edgeIds: Set<string>;
  nodeMessages: Map<string, string>;
  edgeMessages: Map<string, string>;
}

type ValidationHighlight = {
  nodeIds?: string[];
  edgeIds?: string[];
  message: string;
};

function firstValidationMessages(
  entries: ValidationHighlight[],
  field: 'nodeIds' | 'edgeIds',
): Map<string, string> {
  const messages = new Map<string, string>();
  for (const entry of entries) {
    for (const id of entry[field] ?? []) {
      if (!messages.has(id)) messages.set(id, entry.message);
    }
  }
  return messages;
}

export function buildSaveValidationErrors(
  entries: ValidationHighlight[],
): SaveValidationErrors {
  const nodeMessages = firstValidationMessages(entries, 'nodeIds');
  const edgeMessages = firstValidationMessages(entries, 'edgeIds');
  return {
    nodeIds: new Set(nodeMessages.keys()),
    edgeIds: new Set(edgeMessages.keys()),
    nodeMessages,
    edgeMessages,
  };
}

export interface StepNodeActionCallbacks {
  onViewAction?: (workflowDefinitionId: string, stepLabel: string) => void;
  onViewAgent?: (agentDefinitionId: string, stepLabel: string) => void;
  onViewEmailRules?: (emailRuleIds: string[], stepLabel: string) => void;
  onDuplicateNode?: (nodeId: string) => void;
  onRefreshNode?: (nodeId: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  onDisconnectEdges?: (nodeId: string) => void;
  onStaticInputChange?: (
    nodeId: string,
    socketId: string,
    value: unknown,
  ) => void;
  onStaticInputClear?: (nodeId: string, socketId: string) => void;
}

/**
 * The per-node slice of the live validation state (recomputed on every edit,
 * unlike the sticky save errors). Both editors spread this onto step node
 * data, so a new live check lands on both canvases by extending this once.
 */
export interface StepNodeLiveValidation {
  isFloating?: boolean;
  unconnectedRequiredSockets?: Set<string>;
  /** Blocking, recomputed on every edit — see StepNodeData. */
  liveValidationErrorMessage?: string;
  liveValidationErrorSockets?: Set<string>;
  /** Non-blocking fanout-scaling advice for this node, if any. */
  fanoutWarningMessage?: string;
}

export function stepNodeLiveValidation(
  validationState: Pick<
    EditorValidationState,
    | 'floatingNodeIds'
    | 'unconnectedSocketsByStep'
    | 'taskAnchorErrorsByStep'
    | 'taskAnchorErrorSocketsByStep'
    | 'fanoutWarningsByStep'
  >,
  nodeId: string,
): StepNodeLiveValidation {
  return {
    isFloating: validationState.floatingNodeIds.has(nodeId),
    unconnectedRequiredSockets:
      validationState.unconnectedSocketsByStep.get(nodeId),
    liveValidationErrorMessage: validationState.taskAnchorErrorsByStep
      .get(nodeId)
      ?.join('\n\n'),
    liveValidationErrorSockets:
      validationState.taskAnchorErrorSocketsByStep.get(nodeId),
    // Several warnings can name the same node (a nested fanout that is also
    // invariant), so they're joined rather than first-wins.
    fanoutWarningMessage: validationState.fanoutWarningsByStep
      .get(nodeId)
      ?.join('\n\n'),
  };
}

export function decorateStepNodeData<NodeT extends Node>(
  node: NodeT,
  options: StepNodeLiveValidation & {
    editable: boolean;
    saveValidationErrors?: SaveValidationErrors | null;
    actions?: StepNodeActionCallbacks;
  },
): NodeT {
  return {
    ...node,
    data: {
      ...node.data,
      editable: options.editable,
      ...options.actions,
      isFloating: options.isFloating ?? false,
      unconnectedRequiredSockets: options.unconnectedRequiredSockets,
      hasSaveValidationError:
        options.saveValidationErrors?.nodeIds.has(node.id) ?? false,
      saveValidationErrorMessage:
        options.saveValidationErrors?.nodeMessages.get(node.id),
      liveValidationErrorMessage: options.liveValidationErrorMessage,
      liveValidationErrorSockets: options.liveValidationErrorSockets,
      fanoutWarningMessage: options.fanoutWarningMessage,
    },
  };
}

export function decorateEdgeData<EdgeT extends Edge>(
  edge: EdgeT,
  options: {
    editable: boolean;
    saveValidationErrors?: SaveValidationErrors | null;
  },
): EdgeT {
  const edgeId = String(edge.id);
  return {
    ...edge,
    data: {
      ...(edge.data ?? {}),
      editable: options.editable,
      hasSaveValidationError:
        options.saveValidationErrors?.edgeIds.has(edgeId) ?? false,
      saveValidationErrorMessage:
        options.saveValidationErrors?.edgeMessages.get(edgeId),
    },
  };
}
